import {IconButton, Skeleton} from '@mui/material';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import React, {FC} from 'react';
import {useAppSelector} from '../../hooks/storeHooks';
import {Output, RatePlanItem} from '../../contexts/Configuration';
import {calculateAvailableMinutes} from '../../utils/BillingUtils';

const BalanceSummary: FC<BalanceSummaryProps> = ({}) => {
    const credit = useAppSelector((state) => state.billing.balanceCredit) as number;
    const ratePlanItems = useAppSelector((state) => state.billing.ratePlanItems) as RatePlanItem[];
    const waitingForBalance = useAppSelector((state) => state.billing.waitingForBalance) as boolean;


    const sessionStarted = useAppSelector((state) => state.session.sessionStarted) as boolean;
    const outputs = useAppSelector(state => state.session.outputs) as Output[];
    const isTwoWayMode = useAppSelector(state => state.session.isTwoWayMode) as boolean;
    const bidirectionalPanels = useAppSelector(state => state.session.bidirectionalPanels);
    const activeInputPanelId = useAppSelector(state => state.session.activeInputPanelId);

    //calculates the estimated consumed minutes

    // Two-way billing rule: the ACTIVE-input panel is ignored (it's the speaker, not a billed
    // translation target — its textOnly / output device must not be counted). Every OTHER panel
    // counts as an output when it is text-only OR has an output device OR is input-only
    // (INPUT_ONLY_SENTINEL selected). An input-only panel still consumes an audio-translation
    // unit, so it is counted as an audio output (textOnly: false). We derive this directly
    // from the panels (not `state.session.outputs`) so the estimate can't drift out of sync with
    // the live panel config — e.g. two non-active text-only panels now correctly count as 2.
    if (isTwoWayMode) {
        console.log('[AvailableMinutes] panel diagnostics (activeInputPanelId=' + activeInputPanelId + '):');
        bidirectionalPanels.forEach(p => {
            const isActive = p.id === activeInputPanelId;
            const included = !isActive && !!p.language && !!(p.outputDevice || p.textOnly || p.inputOnly);
            console.log(
                `  ${p.id}: active=${isActive} language=${!!p.language}(${p.language?.translationKey ?? '-'}) ` +
                `outputDevice=${!!p.outputDevice} textOnly=${p.textOnly} inputOnly=${p.inputOnly} => ${included ? 'COUNTED' : 'skipped'}`
            );
        });
    }

    // TwoWay billing: backend always charges AUDIO_TRANSLATION regardless of textOnly flag,
    // so force textOnly=false for all panels to match what will actually be charged.
    const billingOutputs: { textOnly?: boolean }[] = isTwoWayMode
        ? bidirectionalPanels
            .filter(p => p.id !== activeInputPanelId && p.language && (p.outputDevice || p.textOnly || p.inputOnly))
            .map(() => ({textOnly: false}))
        : outputs;

    const totalMinAvailable = calculateAvailableMinutes(billingOutputs, ratePlanItems, credit || 0);


    if (waitingForBalance)
        return (
            <div className="nav-bar-balance">
                <div className="nav-bar-balance-credit">
                    <IconButton href="/billing" disabled={sessionStarted}>
                        <LocalMallIcon className="nav-bar-balance-credit-icon"/>
                    </IconButton>
                    <div>Balance</div>
                </div>
                <div className="nav-bar-balance-amount">
                    <Skeleton variant="text"/>
                </div>
                <div className="nav-bar-balance-consumption">
                    <Skeleton variant="text"/>
                </div>
            </div>
        );

    return (
        <div className="nav-bar-balance">
            <div className="nav-bar-balance-credit">
                <IconButton href="/billing" disabled={sessionStarted}>
                    <LocalMallIcon className="nav-bar-balance-credit-icon"/>
                </IconButton>
                <div>Balance</div>
            </div>
            <div className="nav-bar-balance-amount">
                {`$ ${credit}`}
            </div>
            <div className="nav-bar-balance-consumption">
                {`${totalMinAvailable} Available minutes`}
            </div>
        </div>
    );
}

type BalanceSummaryProps = {}

export default BalanceSummary;